(() => {
  if (window.AimiiProducts) return;
  const lifecycle = new AbortController();
  const cache = new Map();
  const sectionIds = new Map();
  const templateSections = new Map();
  const productRequests = new Map();
  const cacheLifetime = 300000, freshLifetime = 30000;
  let cartRevision = 0;
  let currentURL = null;
  const productAccordions = new Map();
  const productProgressMarkup = new WeakMap();
  const productPreparations = new Map();
  const preparationMountVersions = new WeakMap();
  let bundleRuntime = null;
  const reviewInitializers = new Map();
  const reviewRequests = new Map();
  const reviewCache = new Map();
  let judgeMeReady = null;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileViewport = window.matchMedia('(max-width: 900px)');
  let closeTimer = 0;
  let sheetDrag = null;
  let suppressHandleClick = false;
  let pending = null;
  let requestVersion = 0;
  let confirmedCart = null;
  let displayedCart = null;
  let opener = null;
  let historyEntry = null;
  let closingFromHistory = false;
  let previousTitle = null;
  let waitingForHistory = false;
  const closeWaiters = [];
  const finishClose = () => { closeWaiters.splice(0).forEach(resolve => resolve()); };
  const modal = () => document.getElementById('product-dialog');
  const sheetMode = () => mobileViewport.matches && modal()?.dataset.mobilePresentation === 'sheet';
  const listen = (target, type, callback) => target.addEventListener(type, callback, {signal: lifecycle.signal});
  const text = (key, fallback) => modal()?.dataset[key] || fallback;
  const normalize = (href) => {
    const url = new URL(href, location.href);
    url.hash = '';
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    return url;
  };
  const productInCart = (id, variant) => (confirmedCart?.items || []).some(item =>
    String(item.product_id) === id || String(item.variant_id) === variant
  );
  function updateOffers(scope = document) {
    if (!confirmedCart) return;
    scope.querySelectorAll('[data-aimii-bundle-offer]').forEach(offer => {
      offer.hidden = productInCart(offer.dataset.bundleProductId, offer.dataset.bundleVariantId);
    });
  }
  function updateCardCounts(scope = document) {
    const cart = displayedCart || confirmedCart;
    if (!cart) return;
    const cards = Array.from(scope.querySelectorAll('[data-aimii-card]'));
    if (scope.matches?.('[data-aimii-card]')) cards.push(scope);
    cards.forEach(card => {
      const button = card.querySelector('.quickadd');
      if (!button) return;
      const count = (cart.items || []).reduce((total, item) => total + (String(item.product_id) === card.dataset.productId ? Number(item.quantity) || 0 : 0), 0);
      button.classList.toggle('has-items', count > 0);
      let label = button.querySelector('.added-count');
      if (count > 0) {
        if (!label) { label = document.createElement('span'); label.className = 'added-count'; button.prepend(label); }
        label.textContent = count;
      } else label?.remove();
    });
  }
  function updateProductProgress(scope = document) {
    // Product responses can be cached or prefetched. Only the live cart section
    // supplies goal settings/qualification; optimistic cart previews never do.
    const source = document.querySelector('#cart-pane template[data-aimii-product-cart-progress]');
    if (!source) return;
    const markup = source.innerHTML;
    const hosts = [...scope.querySelectorAll('[data-aimii-product-delivery]')];
    if (scope.matches?.('[data-aimii-product-delivery]')) hosts.push(scope);
    hosts.forEach(host => {
      const dialog = host.closest('#product-dialog');
      if (!host.isConnected || (scope === document && dialog && (!dialog.open || dialog.classList.contains('aimii-product-closing'))) || productProgressMarkup.get(host) === markup) return;
      const progress = host.querySelector('[data-aimii-product-progress]');
      const fallback = host.querySelector('[data-aimii-product-delivery-fallback]');
      if (!progress || !fallback) return;
      const previous = progress.querySelector('[data-aimii-product-delivery-details]');
      const expanded = previous?.dataset.expanded === 'true';
      const focusedSummary = previous?.querySelector('summary') === document.activeElement;
      const copy = progress.querySelector('[data-aimii-product-delivery-copy]');
      const focusedCopy = copy?.contains(document.activeElement) ? document.activeElement : null;
      // Move merchant rich text instead of copying it: custom links and IDs retain
      // their identity, including focus after a confirmed cart update.
      if (copy) fallback.append(...copy.childNodes);
      progress.querySelectorAll('.faq-accordion').forEach(group => {
        productAccordions.get(group)?.destroy(); productAccordions.delete(group);
      });
      progress.replaceChildren(source.content.cloneNode(true));
      const nextCopy = progress.querySelector('[data-aimii-product-delivery-copy]');
      const ready = !!nextCopy;
      if (nextCopy) nextCopy.append(...fallback.childNodes);
      const details = progress.querySelector('[data-aimii-product-delivery-details]');
      if (details) details.open = !!expanded;
      fallback.hidden = ready; progress.hidden = !ready;
      host.classList.toggle('aimii-product-delivery-ready', ready);
      productProgressMarkup.set(host, markup);
      initializeAccordions(progress, true);
      if (focusedSummary) details?.querySelector('summary')?.focus({preventScroll: true});
      else focusedCopy?.focus({preventScroll: true});
    });
  }
  function close() {
    if (!modal()?.open && !waitingForHistory) return Promise.resolve();
    return new Promise(resolve => {
      closeWaiters.push(resolve);
      if (modal()?.open) dismiss();
    });
  }
  function cancelDrag() {
    if (sheetDrag?.handle.hasPointerCapture(sheetDrag.id)) sheetDrag.handle.releasePointerCapture(sheetDrag.id);
    sheetDrag = null;
    modal()?.classList.remove('aimii-product-dragging');
    modal()?.style.removeProperty('--aimii-product-drag');
  }
  function clearPresentation() {
    clearTimeout(closeTimer);
    closeTimer = 0;
    cancelDrag();
    modal()?.classList.remove('aimii-product-visible', 'aimii-product-closing');
    modal()?.style.removeProperty('transform');
  }
  function dismiss() {
    const dialog = modal();
    if (!dialog?.open || dialog.classList.contains('aimii-product-closing')) return;
    pending?.abort();
    ++requestVersion;
    disposeReviews(document.getElementById('product-body'));
    disposePreparation(document.getElementById('product-body'));
    window.AimiiBundles?.cancel(document.getElementById('product-body'));
    productAccordions.forEach((handle, group) => { if (dialog.contains(group)) handle.cancel(); });
    if (!sheetMode() || reducedMotion.matches) { dialog.close(); return; }
    // Continue from the current opening/drag position rather than jumping before exit.
    dialog.style.transform = getComputedStyle(dialog).transform;
    cancelDrag();
    dialog.getBoundingClientRect();
    dialog.classList.remove('aimii-product-visible');
    dialog.classList.add('aimii-product-closing');
    dialog.style.removeProperty('transform');
    closeTimer = setTimeout(() => { if (dialog.open) dialog.close(); }, 410);
  }
  function presentationChanged() {
    const dialog = modal();
    if (dialog?.classList.contains('aimii-product-closing')) { dialog.close(); return; }
    cancelDrag();
    dialog?.classList.toggle('aimii-product-visible', !!dialog.open && sheetMode());
  }
  function trim(map, maximum) {
    while (map.size > maximum) map.delete(map.keys().next().value);
  }
  function cardFor(url, source) {
    const card = source?.closest?.('[data-aimii-card]');
    if (card && [...card.querySelectorAll('[data-product-open]')].some(link => normalize(link.href).pathname === url.pathname)) return card;
    return [...document.querySelectorAll('[data-aimii-card]')].find(card =>
      [...card.querySelectorAll('[data-product-open]')].some(link => normalize(link.href).pathname === url.pathname)
    );
  }
  function templateKey(url, source) {
    if (url.searchParams.has('view')) return url.searchParams.get('view');
    const card = cardFor(url, source);
    return card?.hasAttribute('data-template-suffix') ? card.dataset.templateSuffix : null;
  }
  function cachedProduct(url) {
    const key = url.pathname + url.search, entry = cache.get(key);
    if (!entry || Date.now() - entry.time >= cacheLifetime) { cache.delete(key); return null; }
    cache.delete(key); cache.set(key, entry);
    return entry;
  }
  function parseFragment(html) {
    // Template parsing stays inert: speculative HTML must not download its images.
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content.querySelector('[data-aimii-product]');
  }
  async function productFragment(url, signal, fresh = false, background = false, source = null) {
    const key = url.pathname + url.search, saved = cachedProduct(url);
    if (!fresh && saved && !saved.invalidated && Date.now() - saved.time < freshLifetime) return saved.html;
    let entry = productRequests.get(key);
    if (entry?.controller.signal.aborted) { productRequests.delete(key); entry = null; }
    if (!entry) {
      const controller = new AbortController();
      entry = {controller, background, promise: null, timer: 0};
      const revision = cartRevision;
      entry.timer = setTimeout(() => controller.abort(), background ? 3000 : 8000);
      entry.promise = (async () => {
        try {
          const hint = templateKey(url, source);
          const id = sectionIds.get(url.pathname + (url.searchParams.get('view') || '')) || (hint !== null && templateSections.get(hint));
          const requestURL = new URL(url);
          if (id) requestURL.searchParams.set('section_id', id);
          const options = {signal: controller.signal, credentials: 'same-origin', cache: 'no-cache', priority: background ? 'low' : 'high', headers: {'Accept': 'text/html'}};
          let response = await fetch(requestURL, options);
          let fragment = response.ok ? parseFragment(await response.text()) : null;
          if (!fragment && id) {
            response = await fetch(url, options);
            fragment = response.ok ? parseFragment(await response.text()) : null;
          }
          if (!fragment || controller.signal.aborted || lifecycle.signal.aborted) throw new Error('Product section unavailable');
          if (fragment.dataset.sectionId) {
            sectionIds.set(url.pathname + (url.searchParams.get('view') || ''), fragment.dataset.sectionId);
            trim(sectionIds, 10);
            if (fragment.hasAttribute('data-template-suffix')) {
              templateSections.set(fragment.dataset.templateSuffix, fragment.dataset.sectionId); trim(templateSections, 10);
            }
          }
          const html = fragment.outerHTML;
          cache.delete(key); cache.set(key, {html, time: Date.now(), invalidated: revision !== cartRevision}); trim(cache, 10);
          return html;
        } finally {
          clearTimeout(entry.timer);
          if (productRequests.get(key) === entry) productRequests.delete(key);
        }
      })();
      productRequests.set(key, entry);
    } else if (!background && entry.background) {
      entry.background = false;
      clearTimeout(entry.timer); entry.timer = setTimeout(() => entry.controller.abort(), 8000);
    }
    const abort = () => entry.controller.abort();
    if (signal?.aborted) abort();
    signal?.addEventListener('abort', abort, {once: true});
    try { return await entry.promise; }
    finally { signal?.removeEventListener('abort', abort); }
  }
  function uniqueIds(scope) {
    const suffix = '-modal';
    const ids = new Map();
    scope.querySelectorAll('[id]').forEach(element => {
      const old = element.getAttribute('id');
      element.setAttribute('id', old + suffix);
      ids.set(old, old + suffix);
    });
    scope.querySelectorAll('[for], [aria-controls], [aria-labelledby]').forEach(element => {
      for (const name of ['for', 'aria-controls', 'aria-labelledby']) {
        if (element.hasAttribute(name)) element.setAttribute(name, element.getAttribute(name).split(' ').map(id => ids.get(id) || id).join(' '));
      }
    });
    scope.querySelectorAll('a[href^="#"]').forEach(link => {
      const id = link.getAttribute('href').slice(1);
      if (ids.has(id)) link.setAttribute('href', '#' + ids.get(id));
    });
  }
  function reviewLibrary() {
    if (judgeMeReady) return judgeMeReady;
    judgeMeReady = new Promise((resolve, reject) => {
      const deadline = setTimeout(() => finish(new Error('Review library unavailable')), 8000);
      let settled = false;
      const finish = error => {
        if (settled) return;
        settled = true;
        clearTimeout(deadline);
        document.removeEventListener('jdgm.finishLoadingCore', load);
        lifecycle.signal.removeEventListener('abort', cancel);
        error ? reject(error) : resolve(window.jdgm);
      };
      const cancel = () => finish(new DOMException('Product lifecycle ended', 'AbortError'));
      const load = () => {
        const jm = window.jdgm;
        if (!jm?.loadScript) return;
        const entries = [
          ['widget/base.js', ['$']],
          ['widget/common.js', ['initializeWidgets']],
          ['widget/main.js', ['_setupLoadReviewsEventsFor']],
          ['widget/media.js', ['setupMediaGallery']],
          ['widget/form.js', ['_renderAndSetupReviewForm', '_setupQuestionsForm', '_setupFormsSubmit']]
        ];
        const next = index => {
          if (settled) return;
          const entry = entries[index];
          if (!entry) { finish(); return; }
          const [path, functions] = entry;
          const available = () => functions.every(name => typeof jm[name] === 'function');
          if (available()) { next(index + 1); return; }
          jm.loadScript(jm.CDN_HOST + path, () => jm.$(() => {
            if (available()) next(index + 1);
            else finish(new Error('Review initializer unavailable'));
          }));
        };
        try {
          jm.loadCSS?.(jm.widgetPath('base.css'));
          jm.loadCSS?.(jm.widgetPath('main.css'));
          next(0);
        } catch (error) { finish(error); }
      };
      document.addEventListener('jdgm.finishLoadingCore', load);
      lifecycle.signal.addEventListener('abort', cancel, {once: true});
      load();
    });
    return judgeMeReady;
  }
  function disposeReviews(scope) {
    for (const [host, controller] of reviewRequests) {
      if (!scope.contains(host)) continue;
      controller.abort();
      reviewRequests.delete(host);
    }
    for (const [widget, controller] of reviewInitializers) {
      if (!scope.contains(widget)) continue;
      controller.abort();
      reviewInitializers.delete(widget);
      if (widget.dataset.aimiiReviewReady && window.jdgm?.$) window.jdgm.$(widget).remove();
    }
  }
  function loadReviews(scope) {
    scope.querySelectorAll('[data-aimii-product-reviews-host]').forEach(async host => {
      if (reviewRequests.has(host)) return;
      const controller = new AbortController();
      reviewRequests.set(host, controller);
      try {
        const url = normalize(host.dataset.productUrl);
        url.searchParams.set('section_id', 'aimii-product-reviews');
        const key = url.pathname + url.search;
        const saved = reviewCache.get(key);
        let html = saved && Date.now() - saved.time < 30000 ? saved.html : null;
        if (!html) {
          const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin', headers: {'Accept': 'text/html'}});
          if (!response.ok) throw new Error('Product reviews unavailable');
          html = await response.text();
        }
        if (controller.signal.aborted || !host.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const reviews = parsed.querySelector('.aimii-product-reviews');
        const widget = reviews?.querySelector('[data-aimii-review-widget]');
        if (!widget || widget.dataset.productId !== host.dataset.productId) throw new Error('Product review source unavailable');
        reviews.querySelectorAll('script').forEach(script => script.remove());
        widget.id = host.id + '-widget';
        if (host.closest('#product-dialog')) uniqueIds(reviews);
        host.replaceChildren(...Array.from(reviews.childNodes));
        reviewCache.set(key, {html, time: Date.now()});
        trim(reviewCache, 10);
        initializeReviews(host);
      } catch (error) {
        if (controller.signal.aborted || !host.isConnected) return;
        const status = host.querySelector('[data-aimii-reviews-status]');
        if (status) status.textContent = host.dataset.errorLabel;
        host.querySelector('[data-aimii-product-review-fallback]')?.removeAttribute('hidden');
      }
    });
  }
  function initializeReviews(scope) {
    scope.querySelectorAll('[data-aimii-review-widget]').forEach(widget => {
      if (reviewInitializers.has(widget)) return;
      const controller = new AbortController();
      reviewInitializers.set(widget, controller);
      widget.classList.remove('jdgm-review-widget');
      reviewLibrary().then(jm => {
        if (controller.signal.aborted || !widget.isConnected) return;
        widget.classList.add('jdgm-review-widget');
        jm.initializeWidgets(jm.$(widget.parentElement));
        widget.dataset.aimiiReviewReady = 'true';
      }).catch(() => {
        if (!controller.signal.aborted && widget.isConnected) widget.parentElement.querySelector('[data-aimii-product-review-fallback]')?.removeAttribute('hidden');
      });
    });
  }
  function bundleLibrary() {
    if (window.AimiiBundles) return Promise.resolve(window.AimiiBundles);
    bundleRuntime ||= import('@aimii/product-bundles').then(() => window.AimiiBundles).catch(() => { bundleRuntime = null; return null; });
    return bundleRuntime;
  }
  function initializeBundles(product) {
    if (!product.querySelector('.product-copy > [data-aimii-bundle-contents]')) return;
    // Cached rendering runs before showModal(); start only after that synchronous opening finishes.
    bundleLibrary().then(runtime => {
      if (lifecycle.signal.aborted || !product.isConnected || (product.closest('#product-dialog') && (!modal()?.open || modal().classList.contains('aimii-product-closing')))) return;
      if (runtime) runtime.mount(product);
      else {
        const host = product.querySelector('.product-copy > [data-aimii-bundle-contents]');
        if (!host || host.dataset.aimiiBundleSource || host.querySelector('.aimii-bundle-status')) return;
        host.querySelector('[data-aimii-bundle-list]').replaceChildren();
        host.querySelector('[data-aimii-bundle-list]').removeAttribute('aria-busy');
        const link = document.createElement('a'); link.href = product.dataset.productUrl; link.className = 'aimii-bundle-status'; link.textContent = host.dataset.unavailableLabel;
        host.append(link);
      }
    });
  }
  function initializePreparation(product) {
    const mountVersion = (preparationMountVersions.get(product) || 0) + 1;
    preparationMountVersions.set(product, mountVersion);
    // Cached HTML is inserted before showModal(); inert/prefetched fragments never mount.
    queueMicrotask(() => {
      const dialog = product.closest('#product-dialog');
      if (lifecycle.signal.aborted || preparationMountVersions.get(product) !== mountVersion || !product.isConnected || (dialog && (!dialog.open || dialog.classList.contains('aimii-product-closing')))) return;
      productPreparations.forEach((handle, root) => { if (!root.isConnected) handle.destroy(); });
      product.querySelectorAll('[data-aimii-product-preparation]').forEach(root => {
        if (productPreparations.has(root)) return;
        const video = root.querySelector('[data-aimii-preparation-video]');
        const toggle = root.querySelector('[data-aimii-preparation-toggle]');
        if (!video || !toggle) return;
        const controller = new AbortController();
        const on = (target, type, callback) => target?.addEventListener(type, callback, {signal: controller.signal});
        const scroll = dialog ? document.getElementById('product-body') : null;
        let prepared = false, visible = false, intersecting = false, userPaused = false, explicitPlay = false;
        let blocked = false, suppressPause = false, playPending = false, playVersion = 0;
        const live = () => !controller.signal.aborted && root.isConnected && !document.hidden && !root.closest('[hidden], [inert]') && (dialog ? (dialog.open && !dialog.classList.contains('aimii-product-closing')) : !modal()?.open);
        const canPlay = () => live() && !userPaused && (explicitPlay ? intersecting : (visible && root.dataset.autoplay === 'true' && !reducedMotion.matches && !limitedConnection() && !blocked));
        const update = () => {
          const playing = !video.paused;
          const label = playing ? toggle.dataset.pauseText : toggle.dataset.playText;
          toggle.textContent = label || (playing ? 'Video pausieren' : 'Video abspielen');
          toggle.setAttribute('aria-label', toggle.textContent);
          root.classList.toggle('preparation-is-playing', playing);
          root.classList.toggle('preparation-is-blocked', blocked);
        };
        const pause = () => {
          ++playVersion; playPending = false;
          if (!video.paused) { suppressPause = true; video.pause(); }
          update();
        };
        const prepare = (manual = false) => {
          if (prepared || !live() || (!manual && limitedConnection())) return;
          const sources = [...video.querySelectorAll('source[data-src]')];
          if (!sources.length) return;
          prepared = true;
          video.preload = 'metadata';
          sources.forEach(source => { source.src = source.dataset.src; });
          video.load();
        };
        const play = () => {
          if (playPending || !video.paused || !canPlay()) return;
          prepare(explicitPlay);
          if (!prepared) return;
          video.muted = true; video.playsInline = true;
          const version = ++playVersion;
          playPending = true;
          Promise.resolve(video.play()).then(() => {
            if (version !== playVersion || !canPlay()) { if (!canPlay()) pause(); return; }
            blocked = false; update();
          }).catch(() => {
            if (version !== playVersion || controller.signal.aborted) return;
            blocked = true; explicitPlay = false;
            update();
          }).finally(() => { if (version === playVersion) playPending = false; });
        };
        const reconcile = () => {
          if (!live()) { pause(); return; }
          prepare();
          if (canPlay()) play(); else pause();
        };
        const measure = () => {
          const bounds = video.getBoundingClientRect();
          const viewport = scroll ? scroll.getBoundingClientRect() : {top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth};
          const height = Math.max(0, Math.min(bounds.bottom, viewport.bottom, window.innerHeight) - Math.max(bounds.top, viewport.top, 0));
          const width = Math.max(0, Math.min(bounds.right, viewport.right, window.innerWidth) - Math.max(bounds.left, viewport.left, 0));
          intersecting = height > 0 && width > 0;
          visible = intersecting && bounds.width > 0 && bounds.height > 0 && height * width / (bounds.width * bounds.height) >= .35;
          reconcile();
        };
        const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
          intersecting = entries.some(entry => entry.isIntersecting && entry.intersectionRatio > 0);
          visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .35);
          reconcile();
        }, {root: scroll, threshold: [0, .001, .35]}) : null;
        const requestPlay = () => { userPaused = false; explicitPlay = true; blocked = false; measure(); };
        on(toggle, 'click', () => {
          if (!video.paused || playPending) { userPaused = true; explicitPlay = false; pause(); }
          else requestPlay();
        });
        on(video, 'click', () => { if (video.paused && !playPending) requestPlay(); });
        on(video, 'play', () => {
          // Honor explicit playback while retaining visibility and lifecycle guards.
          if (!playPending) { userPaused = false; explicitPlay = true; blocked = false; }
          if (!canPlay()) { pause(); return; }
          update();
        });
        on(video, 'pause', () => {
          if (suppressPause) suppressPause = false;
          else if (live() && intersecting && !video.ended) { userPaused = true; explicitPlay = false; }
          update();
        });
        on(video, 'error', () => { blocked = true; explicitPlay = false; pause(); update(); });
        on(document, 'visibilitychange', measure);
        on(document, 'aimii:product-opening', () => { if (!dialog) pause(); });
        on(document, 'aimii:product-closed', measure);
        on(reducedMotion, 'change', reconcile);
        on(connection, 'change', reconcile);
        if (!observer) on(scroll || window, 'scroll', measure);
        on(window, 'resize', measure);
        const handle = {destroy() {
          if (controller.signal.aborted) return;
          controller.abort(); observer?.disconnect(); pause();
          video.querySelectorAll('source[data-src]').forEach(source => source.removeAttribute('src'));
          video.removeAttribute('src'); video.removeAttribute('poster');
          video.preload = 'none'; video.controls = false;
          if (prepared) video.load();
          toggle.hidden = true;
          root.classList.remove('preparation-is-playing', 'preparation-is-blocked');
          productPreparations.delete(root);
        }};
        productPreparations.set(root, handle);
        if (video.dataset.poster) video.poster = video.dataset.poster;
        video.muted = true; video.playsInline = true; video.controls = false;
        toggle.hidden = false; update();
        if (observer) { observer.observe(video); reconcile(); }
        else measure();
      });
    });
  }
  function disposePreparation(scope) {
    if (!scope) return;
    const products = [...scope.querySelectorAll('[data-aimii-product]')];
    if (scope.matches?.('[data-aimii-product]')) products.push(scope);
    products.forEach(product => preparationMountVersions.set(product, (preparationMountVersions.get(product) || 0) + 1));
    productPreparations.forEach((handle, root) => { if (scope === root || scope.contains(root)) handle.destroy(); });
  }
  function initializeAccordions(product, preserveAccordions = false) {
    product.querySelectorAll('.faq-accordion').forEach(group => {
      if (productAccordions.has(group) || !group.querySelector('[data-aimii-product-accordion]')) return;
      group.querySelectorAll('[data-aimii-product-accordion]').forEach(item => {
        item.dataset.expanded = String(item.open);
        item.querySelector('summary')?.setAttribute('aria-expanded', String(item.open));
      });
      if (!preserveAccordions && !group.hasAttribute('data-aimii-accordion-default-closed') && !group.querySelector('[data-aimii-product-accordion][open]')) {
        const first = group.querySelector('[data-aimii-product-accordion]');
        first.open = true; first.dataset.expanded = 'true'; first.querySelector('summary').setAttribute('aria-expanded', 'true');
      }
      const handle = window.AimiiAccordion?.mount(group, {signal: lifecycle.signal, selector: '[data-aimii-product-accordion]', editor: true});
      if (handle) { productAccordions.set(group, handle); group.dataset.aimiiProductAccordionReady = 'true'; }
    });
  }
  function initialize(scope, preserveAccordions = false) {
    const products = Array.from(scope.querySelectorAll('[data-aimii-product]'));
    if (scope.matches?.('[data-aimii-product]')) products.push(scope);
    products.forEach(product => {
      if (!product.closest('#product-dialog') && product.dataset.productUrl && product.dataset.sectionId) {
        sectionIds.set(new URL(product.dataset.productUrl, location.href).pathname + (new URL(location.href).searchParams.get('view') || ''), product.dataset.sectionId);
        if (product.hasAttribute('data-template-suffix')) { templateSections.set(product.dataset.templateSuffix, product.dataset.sectionId); trim(templateSections, 10); }
        trim(sectionIds, 10);
      }
      if (!product.closest('#product-dialog')) {
        const template = product.querySelector('[data-aimii-product-reviews-template]');
        const existing = Array.from(document.querySelectorAll('.jdgm-review-widget')).some(widget => widget.dataset.id === product.dataset.productId);
        if (template && !existing) template.replaceWith(template.content.cloneNode(true));
      }
      if (product.querySelector('[data-aimii-product-reviews-link]')) product.querySelectorAll('[data-aimii-app-block]').forEach(block => {
        if (block.querySelector('.jdgm-preview-badge')) block.hidden = true;
      });
      updateProductProgress(product);
      initializePreparation(product);
      initializeBundles(product);
      initializeAccordions(product, preserveAccordions);
    });
    loadReviews(scope);
    initializeReviews(scope);
    updateOffers(scope);
    updateCardCounts(scope);
  }
  function disposeAccordions(scope) {
    if (!scope) return;
    disposePreparation(scope);
    window.AimiiBundles?.destroy(scope);
    productAccordions.forEach((handle, group) => {
      if (scope === group || scope.contains(group)) {
        handle.destroy(); productAccordions.delete(group);
        delete group.dataset.aimiiProductAccordionReady;
        group.querySelectorAll('.faq-answer').forEach(answer => answer.style.removeProperty('height'));
      }
    });
  }
  const informationAccordions = scope => [...(scope?.querySelectorAll('[data-aimii-product-accordion]') || [])].filter(item => !item.hasAttribute('data-aimii-product-delivery-details'));
  function renderModal(html, preserve = false, focusBeforeGate = null) {
    const scroll = document.getElementById('product-body');
    const previous = scroll.querySelector('[data-aimii-product]');
    const quantity = preserve && previous?.querySelector('input[name="quantity"]')?.value;
    const states = preserve ? informationAccordions(previous).map(item => item.dataset.expanded === 'true') : [];
    const deliveryExpanded = preserve && previous?.querySelector('[data-aimii-product-delivery-details]')?.dataset.expanded === 'true';
    const selectedPhoto = preserve && previous?.querySelector('[data-aimii-photo][aria-pressed="true"]')?.dataset.aimiiPhoto;
    const position = scroll.scrollTop;
    const active = document.activeElement === document.body && focusBeforeGate?.isConnected ? focusBeforeGate : document.activeElement;
    const focused = preserve && modal().contains(active) && (scroll.contains(active) || document.getElementById('product-footer').contains(active));
    const focusInFooter = focused && !!active.closest('#product-footer');
    const focusId = focused && active.id;
    const focusQuantity = focused && active.dataset.aimiiQuantity;
    const focusName = focused && active.getAttribute('name');
    const focusPhoto = focused && active.dataset.aimiiPhoto;
    const focusDeliverySummary = focused && active.matches('[data-aimii-product-delivery-details]>summary');
    const focusSummary = focused ? informationAccordions(previous).map(item => item.querySelector('summary')).indexOf(active) : -1;
    const focusSubmit = focused && active.matches('button[type="submit"]');
    const dialog = modal();
    const fragment = parseFragment(html);
    const body = fragment?.querySelector('[data-aimii-product-body]');
    const footer = fragment?.querySelector('[data-aimii-product-footer]');
    if (!dialog || !body || !footer) throw new Error('Product dialog unavailable');
    const content = document.createElement('section');
    content.className = fragment.className;
    Object.assign(content.dataset, fragment.dataset);
    content.append(...Array.from(body.childNodes).map(node => node.cloneNode(true)));
    const footerContent = footer.content.cloneNode(true);
    const reviews = content.querySelector('[data-aimii-product-reviews-template]');
    if (reviews) reviews.replaceWith(reviews.content.cloneNode(true));
    content.querySelectorAll('[data-aimii-app-block]').forEach(block => {
      if (block.querySelector('.jdgm-preview-badge')) { block.remove(); return; }
      const link = document.createElement('a');
      link.href = fragment.dataset.productUrl;
      link.className = 'text-link';
      link.textContent = text('openLabel', 'Produktseite öffnen');
      block.replaceChildren(link);
    });
    uniqueIds(content);
    uniqueIds(footerContent);
    if (preserve) {
      informationAccordions(content).forEach((item, index) => { if (index < states.length) item.open = states[index]; });
      const existingReviews = previous?.querySelector('[data-aimii-product-reviews-host]');
      const nextReviews = content.querySelector('[data-aimii-product-reviews-host]');
      if (existingReviews && nextReviews && existingReviews.dataset.productId === nextReviews.dataset.productId) nextReviews.replaceWith(existingReviews);
      const photo = [...content.querySelectorAll('[data-aimii-photo]')].find(button => button.dataset.aimiiPhoto === selectedPhoto);
      if (photo) selectPhoto(photo, content);
    }
    disposeReviews(document.getElementById('product-body'));
    disposeAccordions(document.getElementById('product-body'));
    document.getElementById('product-title').textContent = fragment.dataset.productTitle;
    document.getElementById('product-body').replaceChildren(content);
    document.getElementById('product-footer').replaceChildren(footerContent);
    initialize(content, preserve);
    if (deliveryExpanded) {
      const details = content.querySelector('[data-aimii-product-delivery-details]');
      if (details) { details.open = true; details.dataset.expanded = 'true'; details.querySelector('summary').setAttribute('aria-expanded', 'true'); details.querySelector('.faq-answer').style.height = 'auto'; }
    }
    dialog.removeAttribute('aria-busy');
    delete dialog.dataset.aimiiProductStale;
    if (quantity) modal().querySelectorAll('.aimii-product-form input[name="quantity"]').forEach(input => { input.value = validQuantity(input, quantity); });
    if (focused) {
      const scope = focusInFooter ? document.getElementById('product-footer') : scroll;
      const target = focusId ? document.getElementById(focusId) : focusQuantity ? scope.querySelector(`[data-aimii-quantity="${focusQuantity}"]`) : focusName ? [...scope.querySelectorAll('[name]')].find(element => element.name === focusName) : focusPhoto ? [...scope.querySelectorAll('[data-aimii-photo]')].find(element => element.dataset.aimiiPhoto === focusPhoto) : focusDeliverySummary ? scope.querySelector('[data-aimii-product-delivery-details]>summary') : focusSummary >= 0 ? informationAccordions(scope)[focusSummary]?.querySelector('summary') : focusSubmit ? scope.querySelector('button[type="submit"]') : null;
      target?.focus({preventScroll: true});
    }
    scroll.scrollTop = preserve ? position : 0;
    document.dispatchEvent(new CustomEvent('aimii:product-loaded', {detail: {product: content, modal: true}}));
  }
  function loadingPreview(url, source) {
    disposeReviews(document.getElementById('product-body'));
    disposeAccordions(document.getElementById('product-body'));
    delete modal().dataset.aimiiProductStale;
    const card = cardFor(url, source);
    const title = card?.dataset.title || source?.querySelector?.('h2,h3')?.textContent || text('loading', 'Produkt wird geladen …');
    const preview = document.createElement('section');
    preview.className = 'aimii-main-product'; preview.dataset.aimiiProductSkeleton = '';
    preview.setAttribute('aria-hidden', 'true');
    preview.innerHTML = '<div class="wrap"><div class="product-layout"><div class="product-gallery"><div class="product-image aimii-product-placeholder" data-skeleton-image></div><div class="product-thumbs"><button disabled class="aimii-product-placeholder"></button><button disabled class="aimii-product-placeholder"></button></div></div><div class="product-copy"><div class="eyebrow"><span class="aimii-product-placeholder aimii-skeleton-line"></span></div><h1 data-skeleton-title></h1><div class="aimii-product-placeholder aimii-skeleton-rating"></div><div class="intro"><span class="aimii-product-placeholder aimii-skeleton-line"></span><span class="aimii-product-placeholder aimii-skeleton-line"></span></div><div class="product-meta"><span class="meta aimii-product-placeholder"></span><span class="meta aimii-product-placeholder"></span><span class="meta aimii-product-placeholder"></span></div><div class="product-price"><span class="aimii-product-placeholder aimii-skeleton-price"></span></div><div class="delivery-note aimii-product-placeholder"></div></div></div></div>';
    const image = card?.querySelector('.photo img') || source?.querySelector?.('img');
    if (image?.currentSrc || image?.src) {
      const copy = document.createElement('img'); copy.src = image.currentSrc || image.src; copy.alt = image.alt || title;
      preview.querySelector('[data-skeleton-image]').append(copy);
    }
    if (card?.dataset.mediaCount === '1') preview.querySelector('.product-thumbs').remove();
    const heading = preview.querySelector('[data-skeleton-title]');
    if (title === text('loading', 'Produkt wird geladen …')) { heading.className = 'aimii-product-placeholder aimii-skeleton-title'; }
    else heading.textContent = title;
    document.getElementById('product-title').textContent = title;
    document.getElementById('product-body').replaceChildren(preview);
    const status = document.createElement('p'); status.className = 'aimii-product-status'; status.setAttribute('role', 'status'); status.textContent = text('loading', 'Produkt wird geladen …');
    document.getElementById('product-body').append(status);
    const footer = document.createElement('div'); footer.className = 'aimii-product-form aimii-product-skeleton-footer'; footer.setAttribute('aria-hidden', 'true');
    footer.innerHTML = '<div class="sheet-price"><span class="aimii-product-placeholder aimii-skeleton-price"></span></div><div class="qty aimii-product-placeholder"></div><button type="button" class="btn" disabled><span class="aimii-product-placeholder aimii-skeleton-action"></span></button>';
    document.getElementById('product-footer').replaceChildren(footer);
  }
  function stalePurchase() {
    modal().dataset.aimiiProductStale = 'true';
    modal().querySelectorAll('.aimii-product-form button[type="submit"]').forEach(button => {
      button.disabled = true; button.setAttribute('aria-busy', 'true');
      const label = [...button.childNodes].find(node => node.nodeType === 3);
      if (label) label.textContent = text('checking', 'Wird geprüft …');
    });
  }
  function loadingError(url, cached) {
    const notice = document.createElement('p'); notice.className = 'aimii-product-loading'; notice.setAttribute('role', 'alert');
    notice.textContent = text('error', 'Produkt konnte nicht geladen werden. Bitte öffne die Produktseite.');
    const link = document.createElement('a'); link.className = 'text-link'; link.href = url.href; link.textContent = text('openLabel', 'Produktseite öffnen');
    const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'text-link'; retry.dataset.productRetry = ''; retry.textContent = text('retry', 'Erneut versuchen');
    notice.append(document.createElement('br'), retry, document.createTextNode(' '), link);
    if (cached) document.getElementById('product-body').append(notice);
    else { document.getElementById('product-body').replaceChildren(notice); document.getElementById('product-footer').replaceChildren(); }
    modal().removeAttribute('aria-busy');
  }
  async function open(href, source, fromHistory = false) {
    if (waitingForHistory) await new Promise(resolve => closeWaiters.push(resolve));
    const dialog = modal();
    if (!dialog || typeof dialog.showModal !== 'function') { location.assign(href); return; }
    const url = normalize(href);
    if (url.origin !== location.origin) { location.assign(href); return; }
    document.dispatchEvent(new CustomEvent('aimii:product-opening'));
    stopPrefetch(url); clearTimeout(hoverTimer); hoverProduct = null;
    clearTimeout(closeTimer); closeTimer = 0; cancelDrag();
    if (dialog.classList.contains('aimii-product-closing')) closingFromHistory = false;
    dialog.classList.remove('aimii-product-closing'); dialog.style.removeProperty('transform');
    pending?.abort(); pending = new AbortController();
    const version = ++requestVersion;
    currentURL = url;
    const saved = cachedProduct(url);
    if (!dialog.open) { opener = source || document.activeElement; previousTitle = document.title; }
    if (saved) renderModal(saved.html);
    else loadingPreview(url, source);
    if (!dialog.open) { dialog.showModal(); document.documentElement.classList.add('aimii-product-open'); if (sheetMode()) dialog.getBoundingClientRect(); }
    dialog.classList.toggle('aimii-product-visible', sheetMode());
    const announce = () => {
      const next = {...history.state, aimiiProductModal: true, aimiiProductURL: url.href};
      if (!fromHistory) { if (historyEntry) history.replaceState(next, '', url); else { history.pushState(next, '', url); historyEntry = true; } } else historyEntry = true;
      document.title = document.getElementById('product-title').textContent + ' — ' + (window.Shopify?.shop || 'aimii');
      const title = document.getElementById('product-title'); title.tabIndex = -1; title.focus({preventScroll: true});
    };
    if (saved) announce();
    if (saved && !saved.invalidated && Date.now() - saved.time < freshLifetime) return;
    if (saved) stalePurchase(); else dialog.setAttribute('aria-busy', 'true');
    try {
      let html = await productFragment(url, pending.signal, true, false, source);
      if (version === requestVersion && cachedProduct(url)?.invalidated) html = await productFragment(url, pending.signal, true, false, source);
      if (version !== requestVersion || !dialog.open) return;
      renderModal(html, !!saved);
      if (cachedProduct(url)?.invalidated) { stalePurchase(); loadingError(url, true); }
      if (!saved) announce();
    } catch (error) {
      if (version !== requestVersion || !dialog.open || pending.signal.aborted) return;
      loadingError(url, !!saved);
    }
  }
  function selectPhoto(photo, scope) {
    const image = scope?.querySelector('[data-aimii-main-image] img');
    if (!image) return;
    image.src = photo.dataset.aimiiPhoto; image.srcset = photo.dataset.imageSrcset || ''; image.alt = photo.dataset.imageAlt || '';
    scope.querySelectorAll('[data-aimii-photo]').forEach(button => button.setAttribute('aria-pressed', String(button === photo)));
  }
  function productScope(element) {
    if (modal()?.contains(element)) return document.getElementById('product-body')?.querySelector('[data-aimii-product]');
    return element.closest('[data-aimii-product]');
  }
  function validQuantity(input, value) {
    const minimum = Number(input.min) || 1;
    const increment = Number(input.step) || 1;
    const maximum = input.hasAttribute('max') ? Number(input.max) : Infinity;
    const alignedMaximum = Number.isFinite(maximum) ? minimum + Math.floor((maximum - minimum) / increment) * increment : Infinity;
    const requested = minimum + Math.max(0, Math.floor((Number(value || minimum) - minimum) / increment)) * increment;
    return Math.max(minimum, Math.min(alignedMaximum, requested));
  }
  function syncQuantity(element) {
    const scope = productScope(element);
    if (!scope) return;
    scope.querySelectorAll('.aimii-product-form input[name="quantity"]').forEach(input => { input.value = element.value; });
    if (modal()?.contains(scope)) document.querySelectorAll('#product-footer .aimii-product-form input[name="quantity"]').forEach(input => { input.value = element.value; });
  }
  async function changeVariant(select) {
    const scope = productScope(select);
    if (!scope) return;
    const url = normalize(scope.dataset.productUrl);
    url.searchParams.set('variant', select.value);
    if (modal()?.contains(scope)) {
      const quantity = scope.querySelector('.aimii-product-form input[name="quantity"]')?.value;
      await open(url.href, opener);
      const selectedScope = document.getElementById('product-body')?.querySelector('[data-aimii-product]');
      const input = selectedScope?.querySelector('.aimii-product-form input[name="quantity"]');
      if (input && selectedScope.dataset.productUrl === scope.dataset.productUrl) {
        input.value = validQuantity(input, quantity);
        syncQuantity(input);
      }
      return;
    }
    pending?.abort();
    pending = new AbortController();
    const version = ++requestVersion;
    scope.setAttribute('aria-busy', 'true');
    const quantity = scope.querySelector('.aimii-product-form input[name="quantity"]')?.value;
    try {
      let html = await productFragment(url, pending.signal, true);
      if (version === requestVersion && cachedProduct(url)?.invalidated) html = await productFragment(url, pending.signal, true);
      if (version !== requestVersion || !scope.isConnected) return;
      const fragment = parseFragment(html);
      const existingApps = Array.from(scope.querySelectorAll('[data-aimii-app-block]'));
      fragment.querySelectorAll('[data-aimii-app-block]').forEach((target, index) => {
        if (existingApps[index]) target.replaceWith(existingApps[index]);
      });
      disposeReviews(scope);
      disposeAccordions(scope);
      scope.replaceWith(fragment);
      initialize(fragment);
      const quantityInput = fragment.querySelector('.aimii-product-form input[name="quantity"]');
      if (quantityInput) {
        quantityInput.value = validQuantity(quantityInput, quantity);
      }
      history.replaceState(history.state, '', url);
      fragment.querySelector('[data-aimii-variant]')?.focus({preventScroll: true});
      document.dispatchEvent(new CustomEvent('aimii:product-loaded', {detail: {product: fragment, modal: false}}));
    } catch (error) {
      if (error.name !== 'AbortError' && version === requestVersion) location.assign(url.href);
    } finally { scope.removeAttribute('aria-busy'); }
  }
  listen(document, 'click', event => {
    const reviewLink = event.target.closest?.('[data-aimii-product-reviews-link]');
    if (reviewLink && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      const target = document.getElementById(reviewLink.getAttribute('href').slice(1));
      if (target) {
        event.preventDefault();
        const scroller = target.closest('#product-body');
        const behavior = reducedMotion.matches ? 'instant' : 'smooth';
        const heading = target.querySelector('h2') || target;
        heading.tabIndex = -1;
        heading.focus({preventScroll: true});
        if (scroller) scroller.scrollTo({top: scroller.scrollTop + target.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 12, behavior});
        else target.scrollIntoView({block: 'start', behavior});
        return;
      }
    }
    if (event.target.closest?.('[data-product-retry]')) { event.preventDefault(); if (currentURL) { if (document.querySelector('#product-body [data-aimii-product]')) refreshDisplayed(); else open(currentURL.href, opener); } return; }
    if (event.target.closest?.('[data-product-close], [data-product-handle]')) {
      event.preventDefault();
      dismiss();
      return;
    }
    const target = event.target.closest?.('a[data-product-open]');
    if (target && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!target.target || target.target === '_self') && !target.hasAttribute('download')) {
      const url = new URL(target.href);
      if (url.origin === location.origin && !window.Shopify?.designMode) { event.preventDefault(); open(url.href, target); return; }
    }
    const quantity = event.target.closest?.('[data-aimii-quantity]');
    if (quantity) {
      const input = quantity.closest('form')?.querySelector('input[name="quantity"]');
      if (!input) return;
      const increment = Number(input.step) || 1;
      input.value = validQuantity(input, Number(input.value || input.min || 1) + Number(quantity.dataset.aimiiQuantity) * increment);
      syncQuantity(input);
      return;
    }
    const photo = event.target.closest?.('[data-aimii-photo]');
    if (photo) selectPhoto(photo, productScope(photo));
  });
  listen(document, 'change', event => {
    if (event.target.matches('[data-aimii-variant]')) changeVariant(event.target);
    if (event.target.matches('.aimii-product-form input[name="quantity"]')) syncQuantity(event.target);
  });
  async function refreshDisplayed() {
    document.querySelectorAll('#product-body .aimii-product-loading[role="alert"]').forEach(notice => notice.remove());
    const url = currentURL, version = ++requestVersion, focusBeforeGate = document.activeElement;
    pending?.abort(); pending = new AbortController(); stalePurchase();
    try {
      let html = await productFragment(url, pending.signal, true);
      if (version === requestVersion && cachedProduct(url)?.invalidated) html = await productFragment(url, pending.signal, true);
      if (version !== requestVersion || !modal()?.open) return;
      renderModal(html, true, focusBeforeGate);
      if (cachedProduct(url)?.invalidated) { stalePurchase(); loadingError(url, true); }
    } catch (error) {
      if (version === requestVersion && modal()?.open && !pending.signal.aborted) loadingError(url, true);
    }
  }
  document.addEventListener('submit', event => {
    if (!modal()?.contains(event.target) || !event.target.matches('.aimii-product-form')) return;
    const saved = currentURL && cachedProduct(currentURL);
    const stale = modal().dataset.aimiiProductStale === 'true';
    if (stale || !saved || saved.invalidated || Date.now() - saved.time >= freshLifetime) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (!stale && currentURL) refreshDisplayed();
    }
  }, {capture: true, signal: lifecycle.signal});
  listen(document, 'submit', event => {
    if (event.target.matches('[data-aimii-variant-form]')) {
      event.preventDefault();
      changeVariant(event.target.querySelector('[data-aimii-variant]'));
    }
  });
  listen(document, 'aimii:cart-update', event => {
    confirmedCart = event.detail?.cart || null;
    displayedCart = confirmedCart;
    cartRevision++;
    window.AimiiBundles?.invalidate();
    cache.forEach(entry => { entry.invalidated = true; });
    updateOffers();
    updateCardCounts();
    updateProductProgress();
  });
  listen(document, 'aimii:cart-preview', event => {
    displayedCart = event.detail?.cart || confirmedCart;
    updateCardCounts();
  });
  listen(document, 'aimii:bundle-loaded', event => { if (event.target.matches?.('[data-aimii-product]')) initializeAccordions(event.target); });
  listen(document, 'aimii:products:rendered', event => { const root = event.detail?.root || document; initialize(root); observeProducts(root); });
  listen(window, 'popstate', event => {
    if (event.state?.aimiiProductModal && event.state.aimiiProductURL) open(event.state.aimiiProductURL, opener, true);
    else {
      if (modal()?.open) { closingFromHistory = true; historyEntry = null; dismiss(); }
      if (waitingForHistory) { waitingForHistory = false; finishClose(); }
    }
  });
  function bindDialog() {
    const dialog = modal();
    if (!dialog || dialog.dataset.aimiiProductBound) return;
    dialog.dataset.aimiiProductBound = 'true';
    listen(dialog, 'click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dismiss();
    });
    listen(dialog, 'cancel', event => { event.preventDefault(); dismiss(); });
    listen(dialog, 'transitionend', event => {
      if (event.target === dialog && event.propertyName === 'transform' && dialog.classList.contains('aimii-product-closing')) dialog.close();
    });
    listen(dialog, 'pointerdown', event => {
      const handle = event.target.closest('[data-product-handle]');
      if (!handle || !dialog.open || !sheetMode() || !event.isPrimary || event.button !== 0 || dialog.classList.contains('aimii-product-closing')) return;
      suppressHandleClick = false;
      sheetDrag = {id: event.pointerId, y: event.clientY, dy: 0, handle};
      handle.setPointerCapture(event.pointerId);
      dialog.classList.add('aimii-product-dragging');
    });
    listen(dialog, 'pointermove', event => {
      if (sheetDrag?.id !== event.pointerId) return;
      sheetDrag.dy = Math.max(0, event.clientY - sheetDrag.y);
      dialog.style.setProperty('--aimii-product-drag', `${sheetDrag.dy}px`);
    });
    listen(dialog, 'pointerup', event => {
      if (sheetDrag?.id !== event.pointerId) return;
      const distance = sheetDrag.dy;
      // A drag may synthesize a click on the close handle; suppress that one click.
      suppressHandleClick = distance > 5;
      if (distance > 75) dismiss();
      else cancelDrag();
    });
    dialog.addEventListener('click', event => {
      if (!event.target.closest('[data-product-handle]')) return;
      const suppress = suppressHandleClick && event.detail > 0;
      suppressHandleClick = false;
      if (suppress) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, {capture: true, signal: lifecycle.signal});
    listen(dialog, 'pointercancel', cancelDrag);
    listen(dialog, 'lostpointercapture', event => { if (sheetDrag?.id === event.pointerId) cancelDrag(); });
    listen(dialog, 'close', () => {
      clearPresentation();
      pending?.abort();
      disposeReviews(document.getElementById('product-body'));
      disposeAccordions(document.getElementById('product-body'));
      ++requestVersion;
      document.documentElement.classList.remove('aimii-product-open');
      if (previousTitle) document.title = previousTitle;
      const navigateBack = historyEntry && !closingFromHistory && history.state?.aimiiProductModal;
      if (navigateBack) { waitingForHistory = true; history.back(); }
      historyEntry = null;
      closingFromHistory = false;
      if (opener?.isConnected) opener.focus({preventScroll: true});
      if (!navigateBack) finishClose();
      document.dispatchEvent(new CustomEvent('aimii:product-closed'));
      scheduleProductPrefetch();
    });
  }
  listen(mobileViewport, 'change', presentationChanged);
  listen(reducedMotion, 'change', presentationChanged);
  function invalidateProductRequests() {
    stopPrefetch(); pending?.abort(); ++requestVersion;
    productRequests.forEach(entry => entry.controller.abort()); productRequests.clear();
    cache.clear(); templateSections.clear(); sectionIds.clear();
  }
  listen(document, 'shopify:section:load', event => {
    invalidateProductRequests(); disposePreparation(event.target); window.AimiiBundles?.invalidate(); window.AimiiBundles?.destroy(event.target); initialize(event.target);
    if (event.target.querySelector('template[data-aimii-product-cart-progress]')) updateProductProgress();
    const product = event.target.matches?.('[data-aimii-product]') ? event.target : event.target.querySelector('[data-aimii-product]');
    if (product?.closest('#product-dialog') && currentURL && normalize(product.dataset.productUrl).pathname === currentURL.pathname && product.dataset.sectionId) {
      sectionIds.set(currentURL.pathname + (currentURL.searchParams.get('view') || ''), product.dataset.sectionId);
      if (product.hasAttribute('data-template-suffix')) templateSections.set(product.dataset.templateSuffix, product.dataset.sectionId);
    }
    observeProducts(event.target); bindDialog();
  });
  listen(document, 'shopify:section:unload', event => {
    if (event.target.contains(modal())) { clearPresentation(); if (modal()?.open) modal().close(); }
    invalidateProductRequests(); clearTimeout(hoverTimer);
    observedBundles.forEach(link => { if (event.target.contains(link)) { bundleObserver?.unobserve(link); observedBundles.delete(link); visibleBundles.delete(link); } });
    observedProducts.forEach(card => { if (event.target.contains(card)) { prefetchObserver?.unobserve(card); observedProducts.delete(card); visibleProducts.delete(card); } });
    disposeReviews(event.target);
    disposeAccordions(event.target);
    if (event.target.querySelector('[data-aimii-product]')) {
      pending?.abort();
    }
  });
  let prefetchJob = null, prefetchTimer = 0, prefetchIdle = null, prefetchStartTimer = 0, hoverTimer = 0;
  let prefetchLoaded = false, automaticPrefetches = 0, bundlePrefetched = false;
  const warmedPreparationPosters = new Set();
  const visibleProducts = new Set(), observedProducts = new Set(), attemptedProducts = new Set();
  let hoverProduct = null;
  const visibleBundles = new Set(), observedBundles = new Set();
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const limitedConnection = () => !!connection?.saveData || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '') || (connection?.downlink > 0 && connection.downlink < 1);
  function stopPrefetch(keepURL = null) {
    clearTimeout(prefetchTimer); prefetchTimer = 0;
    if (prefetchIdle !== null) window.cancelIdleCallback?.(prefetchIdle);
    prefetchIdle = null;
    if (prefetchJob && (!keepURL || prefetchJob.url.href !== keepURL.href)) {
      const entry = productRequests.get(prefetchJob.url.pathname + prefetchJob.url.search);
      if (!entry || entry.background) prefetchJob.controller.abort();
    }
  }
  function eligibleProduct(card) {
    if (!card.isConnected || card.closest('#product-dialog') || !card.getClientRects().length) return false;
    const bounds = card.getBoundingClientRect();
    return bounds.bottom > 0 && bounds.top < window.innerHeight && bounds.right > 0 && bounds.left < window.innerWidth;
  }
  async function warmPreparationPoster(html, job, candidate) {
    if (warmedPreparationPosters.size || job.controller.signal.aborted || lifecycle.signal.aborted || document.hidden || limitedConnection() || window.Shopify?.designMode || modal()?.open || !eligibleProduct(candidate) || document.querySelector('[data-aimii-catalog-busy]') || [...productRequests.values()].some(entry => !entry.background)) return;
    // Inspect inert native HTML only; neither a video nor its sources are mounted here.
    const poster = parseFragment(html)?.querySelector('[data-aimii-product-preparation] [data-aimii-preparation-video]')?.dataset.poster;
    if (!poster) return;
    let url;
    try { url = new URL(poster, location.href); } catch (_) { return; }
    if (!/^https?:$/.test(url.protocol)) return;
    warmedPreparationPosters.add(url.href);
    const controller = new AbortController();
    const abort = () => controller.abort();
    job.controller.signal.addEventListener('abort', abort, {once: true});
    lifecycle.signal.addEventListener('abort', abort, {once: true});
    const timeout = setTimeout(abort, 3000);
    try {
      const response = await fetch(url.href, {signal: controller.signal, priority: 'low', credentials: 'omit'});
      if (response.ok) await response.blob();
    } catch (_) {}
    finally {
      clearTimeout(timeout);
      job.controller.signal.removeEventListener('abort', abort);
      lifecycle.signal.removeEventListener('abort', abort);
    }
  }
  function scheduleProductPrefetch() {
    if (lifecycle.signal.aborted || !prefetchLoaded || document.hidden || limitedConnection() || window.Shopify?.designMode || modal()?.open || prefetchJob || prefetchTimer || prefetchIdle !== null) return;
    const cards = [...visibleProducts].filter(eligibleProduct).sort((a,b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
    const bundleCandidate = !bundlePrefetched ? [...visibleBundles].filter(eligibleProduct).sort((a,b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0] : null;
    const candidate = hoverProduct?.isConnected ? hoverProduct : bundleCandidate || (automaticPrefetches < 3 ? cards.find(card => {
      const link = card.querySelector('[data-product-open]');
      if (!link) return false;
      const targetURL = normalize(link.href);
      if (attemptedProducts.has(targetURL.href)) return false;
      const saved = cachedProduct(targetURL);
      return !saved || saved.invalidated || Date.now() - saved.time >= freshLifetime;
    })?.querySelector('[data-product-open]') : null);
    if (!candidate) return;
    if (document.querySelector('[data-aimii-catalog-busy]') || [...productRequests.values()].some(entry => !entry.background)) {
      prefetchTimer = setTimeout(() => { prefetchTimer = 0; scheduleProductPrefetch(); }, 250); return;
    }
    const run = async () => {
      prefetchIdle = null; prefetchTimer = 0;
      if (lifecycle.signal.aborted || document.hidden || limitedConnection() || modal()?.open || !candidate.isConnected) return;
      if (candidate.matches('[data-aimii-bundle-prefetch]') && !eligibleProduct(candidate)) { scheduleProductPrefetch(); return; }
      if (document.querySelector('[data-aimii-catalog-busy]')) { scheduleProductPrefetch(); return; }
      const url = normalize(candidate.href);
      if (url.origin !== location.origin) return;
      const isHover = hoverProduct === candidate;
      const isBundle = candidate.matches('[data-aimii-bundle-prefetch]') && !bundlePrefetched;
      if (isHover) hoverProduct = null;
      if (isBundle) bundlePrefetched = true;
      else if (!isHover) { automaticPrefetches++; attemptedProducts.add(url.href); }
      const job = {controller: new AbortController(), url}; prefetchJob = job;
      try {
        const html = await productFragment(url, job.controller.signal, false, true, candidate);
        if (isBundle && !job.controller.signal.aborted) {
          const product = parseFragment(html);
          if (product?.querySelector('.product-copy > [data-aimii-bundle-contents]')) {
            const runtime = await bundleLibrary();
            if (runtime && !job.controller.signal.aborted) await runtime.prefetch({variantId: product.dataset.variantId, productUrl: product.dataset.productUrl}, job.controller.signal);
          }
        }
        await warmPreparationPoster(html, job, candidate);
      } catch (_) {}
      finally { if (prefetchJob === job) prefetchJob = null; scheduleProductPrefetch(); }
    };
    if (window.requestIdleCallback && window.cancelIdleCallback) prefetchIdle = window.requestIdleCallback(run);
    else prefetchTimer = setTimeout(run, 200);
  }
  const prefetchObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) visibleProducts.add(entry.target); else visibleProducts.delete(entry.target); });
    scheduleProductPrefetch();
  }, {threshold: .15}) : null;
  const bundleObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) visibleBundles.add(entry.target); else visibleBundles.delete(entry.target); });
    scheduleProductPrefetch();
  }, {threshold: .15}) : null;
  function observeProducts(scope) {
    observedBundles.forEach(link => { if (!link.isConnected) { bundleObserver?.unobserve(link); observedBundles.delete(link); visibleBundles.delete(link); } });
    const bundles = [...scope.querySelectorAll('[data-aimii-bundle-prefetch]')];
    if (scope.matches?.('[data-aimii-bundle-prefetch]')) bundles.push(scope);
    bundles.forEach(link => {
      if (link.closest('#product-dialog') || observedBundles.has(link)) return;
      observedBundles.add(link); if (bundleObserver) bundleObserver.observe(link); else visibleBundles.add(link);
    });
    observedProducts.forEach(card => { if (!card.isConnected) { prefetchObserver?.unobserve(card); observedProducts.delete(card); visibleProducts.delete(card); } });
    const cards = [...scope.querySelectorAll('[data-aimii-card]')];
    if (scope.matches?.('[data-aimii-card]')) cards.push(scope);
    cards.forEach(card => {
      if (card.closest('#product-dialog') || observedProducts.has(card)) return;
      observedProducts.add(card);
      if (prefetchObserver) prefetchObserver.observe(card); else visibleProducts.add(card);
    });
    [...visibleProducts].filter(card => !card.isConnected).forEach(card => visibleProducts.delete(card));
    scheduleProductPrefetch();
  }
  const startPrefetch = () => {
    prefetchStartTimer = setTimeout(() => { prefetchLoaded = true; observeProducts(document); }, 500);
  };
  if (document.readyState === 'complete') startPrefetch(); else listen(window, 'load', startPrefetch);
  listen(document, 'pointerover', event => {
    if (event.pointerType !== 'mouse') return;
    const link = event.target.closest?.('[data-product-open]');
    if (!link || link.closest('#product-dialog') || link.contains(event.relatedTarget)) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => { hoverProduct = link; stopPrefetch(normalize(link.href)); scheduleProductPrefetch(); }, 150);
  });
  listen(document, 'pointerout', event => {
    const link = event.target.closest?.('[data-product-open]');
    if (link && !link.contains(event.relatedTarget)) { clearTimeout(hoverTimer); if (hoverProduct === link) hoverProduct = null; }
  });
  listen(document, 'aimii:catalog-request', () => stopPrefetch());
  listen(document, 'visibilitychange', () => { if (document.hidden) stopPrefetch(); else scheduleProductPrefetch(); });
  if (connection) listen(connection, 'change', () => { if (limitedConnection()) stopPrefetch(); else scheduleProductPrefetch(); });
  listen(window, 'scroll', scheduleProductPrefetch);
  observeProducts(document);
  initialize(document);
  bindDialog();
  document.documentElement.classList.add('aimii-product-js');
  window.AimiiProducts = {open, close, get prefetching() { return !!prefetchJob; }, destroy() {
    pending?.abort();
    clearPresentation();
    disposeReviews(document);
    disposeAccordions(document);
    const returnInFlight = waitingForHistory;
    const navigateBack = historyEntry && !closingFromHistory && history.state?.aimiiProductModal;
    // Native close events are asynchronous. Teardown must restore history itself
    // after removing the controller, without letting a stale close event run later.
    lifecycle.abort();
    if (modal()?.open) modal().close();
    if (previousTitle) document.title = previousTitle;
    if (opener?.isConnected) opener.focus({preventScroll: true});
    if (returnInFlight || navigateBack) {
      window.addEventListener('popstate', () => { waitingForHistory = false; finishClose(); }, {once: true});
      if (!returnInFlight) { waitingForHistory = true; history.back(); }
    } else finishClose();
    cache.clear();
    window.AimiiBundles?.invalidate();
    sectionIds.clear();
    templateSections.clear();
    productRequests.forEach(entry => entry.controller.abort()); productRequests.clear();
    stopPrefetch(); bundleObserver?.disconnect(); observedBundles.clear(); visibleBundles.clear(); prefetchObserver?.disconnect(); observedProducts.clear(); visibleProducts.clear(); attemptedProducts.clear(); hoverProduct = null;
    clearTimeout(prefetchStartTimer); clearTimeout(hoverTimer);
    reviewCache.clear();
    document.documentElement.classList.remove('aimii-product-js', 'aimii-product-open');
    if (modal()) delete modal().dataset.aimiiProductBound;
    delete window.AimiiProducts;
  }};
})();
